import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import PhoneInput from './PhoneInput';

export default function PlayerProfile() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const updateProfile = useMutation(api.auth.updateProfile);
  const requestPasswordReset = useMutation(api.auth.requestPasswordReset);

  const [formData, setFormData] = useState({
    name: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    countryCode: '+52',
    profilePicture: '',
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [imageProcessing, setImageProcessing] = useState(false);

  useEffect(() => {
    if (user) {
      // Parse phone number to separate country code and number
      let countryCode = '+52';
      let phoneNumber = user.phoneNumber;

      if (user.phoneNumber.startsWith('+1')) {
        countryCode = '+1';
        phoneNumber = user.phoneNumber.substring(2);
      } else if (user.phoneNumber.startsWith('+52')) {
        countryCode = '+52';
        phoneNumber = user.phoneNumber.substring(3);
      }

      setFormData({
        name: user.name || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber,
        countryCode,
        profilePicture: user.profilePicture || '',
      });
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    setLoading(true);
    try {
      const updateData: any = {
        playerId: user!._id,
        name: formData.name,
      };

      if (formData.firstName) updateData.firstName = formData.firstName;
      if (formData.lastName) updateData.lastName = formData.lastName;
      if (formData.profilePicture) updateData.profilePicture = formData.profilePicture;

      await updateProfile(updateData);

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Error al actualizar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setError('');
    setPasswordResetSent(false);
    setLoading(true);

    try {
      const fullPhone = user!.phoneNumber;
      await requestPasswordReset({ phoneNumber: fullPhone });
      setPasswordResetSent(true);
      setTimeout(() => setPasswordResetSent(false), 5000);
    } catch (err: any) {
      setError(err.message || 'Error al enviar el enlace');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => {
    signOut();
    navigate('/');
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 5MB for processing)
      if (file.size > 5 * 1024 * 1024) {
        setError('La imagen es demasiado grande. Por favor selecciona una imagen menor a 5MB.');
        return;
      }

      setImageProcessing(true);
      setError('');

      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          // Create canvas for compression
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Calculate new dimensions (max 400x400)
          let width = img.width;
          let height = img.height;
          const maxSize = 400;

          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;

          // Draw and compress image
          ctx?.drawImage(img, 0, 0, width, height);

          // Convert to base64 with compression
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // Convert blob to base64
                const compressedReader = new FileReader();
                compressedReader.onloadend = () => {
                  const base64 = compressedReader.result as string;

                  // Check if compressed image is under 1MB
                  const sizeInMB = (base64.length * 0.75) / (1024 * 1024);
                  if (sizeInMB > 0.9) {
                    // If still too large, compress more
                    canvas.toBlob(
                      (smallerBlob) => {
                        if (smallerBlob) {
                          const finalReader = new FileReader();
                          finalReader.onloadend = () => {
                            setFormData({ ...formData, profilePicture: finalReader.result as string });
                            setError('');
                            setImageProcessing(false);
                          };
                          finalReader.readAsDataURL(smallerBlob);
                        }
                      },
                      'image/jpeg',
                      0.5 // Lower quality for smaller size
                    );
                  } else {
                    setFormData({ ...formData, profilePicture: base64 });
                    setError('');
                    setImageProcessing(false);
                  }
                };
                compressedReader.readAsDataURL(blob);
              }
            },
            'image/jpeg',
            0.8 // 80% quality
          );
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Mi Perfil</h1>
            <div className="flex gap-4">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Volver
              </button>
              <button
                onClick={handleSignOut}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Cerrar Sesión
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-500 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 text-green-600 rounded-lg">
              Perfil actualizado exitosamente
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-200">
                  {imageProcessing ? (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                    </div>
                  ) : formData.profilePicture ? (
                    <img
                      src={formData.profilePicture}
                      alt="Foto de perfil"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                <label className="absolute bottom-0 right-0 bg-green-600 text-white rounded-full p-2 cursor-pointer hover:bg-green-700">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={imageProcessing}
                  />
                </label>
              </div>
            </div>

            {imageProcessing && (
              <div className="text-center mb-4 text-sm text-gray-600">
                Procesando imagen...
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nickname *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Opcional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Apellido
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Opcional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Teléfono
                </label>
                <PhoneInput
                  countryCode={formData.countryCode}
                  phoneNumber={formData.phoneNumber}
                  onCountryCodeChange={(code) => setFormData({ ...formData, countryCode: code })}
                  onPhoneNumberChange={(number) => setFormData({ ...formData, phoneNumber: number })}
                />
                <p className="mt-1 text-sm text-gray-500">
                  El número de teléfono no se puede cambiar
                </p>
              </div>
            </div>


            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </form>

          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Seguridad</h3>

            {passwordResetSent && (
              <div className="mb-4 p-3 bg-green-50 text-green-600 rounded-lg">
                Se ha enviado un enlace para cambiar tu contraseña por WhatsApp
              </div>
            )}

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-4">
                Para cambiar tu contraseña, te enviaremos un enlace seguro por WhatsApp.
              </p>
              <button
                type="button"
                onClick={handlePasswordReset}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10h5v-2h-5c-4.34 0-8-3.66-8-8s3.66-8 8-8 8 3.66 8 8v1.43c0 .79-.71 1.57-1.5 1.57s-1.5-.78-1.5-1.57V12c0-2.76-2.24-5-5-5s-5 2.24-5 5 2.24 5 5 5c1.38 0 2.64-.56 3.54-1.47.65.89 1.77 1.47 2.96 1.47 1.97 0 3.5-1.6 3.5-3.57V12c0-5.52-4.48-10-10-10zm0 13c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>
                </svg>
                {loading ? 'Enviando...' : 'Cambiar Contraseña por WhatsApp'}
              </button>
            </div>
          </div>

          {user.waitList && (
            <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-yellow-800">
                  Estás en la lista de espera para ser organizador de torneos
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}